/**
 * Enhanced LocalStorage Adapter
 * Improved localStorage adapter with event integration and better error handling
 */

import { StorageAdapter, StorageResult, StorageOptions } from '../types'
import { softDeleteVia, restoreVia, listTrashVia, type TrashPrimitives } from '../trash';

export class LocalStorageAdapter implements StorageAdapter {
  private prefix: string
  private eventEmitter: any // Will be injected by the storage registry

  constructor(prefix: string = 'freshcards:', eventEmitter?: any) {
    this.prefix = prefix
    this.eventEmitter = eventEmitter
  }

  private getKey(key: string): string {
    return this.prefix + key
  }

  async save(key: string, value: any, options: StorageOptions = {}): Promise<void> {
    const startTime = Date.now()
    const adapterKey = this.getKey(key)
    
    try {
      // Serialize with date handling
      const serialized = JSON.stringify(value, this.dateReplacer)
      localStorage.setItem(adapterKey, serialized)
      
      // Emit event if enabled
      if (options.event !== false && this.eventEmitter) {
        this.eventEmitter.emit('storage:save', {
          key,
          data: value,
          adapter: this.getName(),
          timestamp: new Date(),
          size: serialized.length
        })
      }
      
      // Log operation
      console.log(`[LocalStorage] Saved ${key} (${serialized.length} bytes)`)
    } catch (error) {
      console.error(`[LocalStorage] Failed to save ${key}:`, error)
      
      // Emit error event
      if (this.eventEmitter) {
        this.eventEmitter.emit('storage:error', {
          error: error as Error,
          operation: 'save',
          key,
          adapter: this.getName()
        })
      }
      
      throw error
    }
  }

  async load(key: string, options: StorageOptions = {}): Promise<any> {
    const startTime = Date.now()
    const adapterKey = this.getKey(key)
    
    try {
      const serialized = localStorage.getItem(adapterKey)
      if (!serialized) {
        console.log(`[LocalStorage] Key ${key} not found`)
        return null
      }
      
      const parsed = JSON.parse(serialized, this.dateReviver)
      
      // Emit event if enabled
      if (options.event !== false && this.eventEmitter) {
        this.eventEmitter.emit('storage:load', {
          key,
          data: parsed,
          adapter: this.getName(),
          timestamp: new Date(),
          size: serialized.length
        })
      }
      
      // Log operation
      console.log(`[LocalStorage] Loaded ${key} (${serialized.length} bytes) in ${Date.now() - startTime}ms`)
      
      return parsed
    } catch (error) {
      console.error(`[LocalStorage] Failed to load ${key}:`, error)
      
      // Emit error event
      if (this.eventEmitter) {
        this.eventEmitter.emit('storage:error', {
          error: error as Error,
          operation: 'load',
          key,
          adapter: this.getName()
        })
      }
      
      return null
    }
  }

  async delete(key: string, options: StorageOptions = {}): Promise<void> {
    const adapterKey = this.getKey(key)
    
    try {
      const existed = localStorage.getItem(adapterKey) !== null
      localStorage.removeItem(adapterKey)
      
      // Emit event if enabled
      if (options.event !== false && this.eventEmitter) {
        this.eventEmitter.emit('storage:delete', {
          key,
          adapter: this.getName(),
          timestamp: new Date(),
          existed
        })
      }
      
      console.log(`[LocalStorage] Deleted ${key}`)
    } catch (error) {
      console.error(`[LocalStorage] Failed to delete ${key}:`, error)
      
      // Emit error event
      if (this.eventEmitter) {
        this.eventEmitter.emit('storage:error', {
          error: error as Error,
          operation: 'delete',
          key,
          adapter: this.getName()
        })
      }
      
      throw error
    }
  }

  async list(prefix: string = '', options: StorageOptions = {}): Promise<string[]> {
    try {
      const keys: string[] = []
      const fullPrefix = this.getKey(prefix)
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(fullPrefix)) {
          keys.push(key.substring(this.prefix.length))
        }
      }
      
      console.log(`[LocalStorage] Listed ${keys.length} keys with prefix '${prefix}'`)
      return keys
    } catch (error) {
      console.error(`[LocalStorage] Failed to list keys with prefix '${prefix}':`, error)
      
      // Emit error event
      if (this.eventEmitter) {
        this.eventEmitter.emit('storage:error', {
          error: error as Error,
          operation: 'list',
          adapter: this.getName()
        })
      }
      
      return []
    }
  }

  async export(): Promise<Blob> {
    try {
      const data: { [key: string]: any } = {}
      let totalSize = 0
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.prefix)) {
          const value = localStorage.getItem(key)
          if (value) {
            const dataKey = key.substring(this.prefix.length)
            data[dataKey] = JSON.parse(value, this.dateReviver)
            totalSize += value.length
          }
        }
      }
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { 
        type: 'application/json' 
      })
      
      console.log(`[LocalStorage] Exported ${Object.keys(data).length} items (${totalSize} bytes)`)
      return blob
    } catch (error) {
      console.error('[LocalStorage] Failed to export:', error)
      
      // Emit error event
      if (this.eventEmitter) {
        this.eventEmitter.emit('storage:error', {
          error: error as Error,
          operation: 'export',
          adapter: this.getName()
        })
      }
      
      throw error
    }
  }

  async import(data: Blob, options: StorageOptions = {}): Promise<void> {
    try {
      const text = await data.text()
      const importedData = JSON.parse(text)
      let importedCount = 0
      
      for (const [key, value] of Object.entries(importedData)) {
        await this.save(key, value, { ...options, event: false }) // Avoid recursive events
        importedCount++
      }
      
      // Emit import event
      if (this.eventEmitter) {
        this.eventEmitter.emit('storage:import', {
          size: data.size,
          count: importedCount,
          adapter: this.getName(),
          timestamp: new Date()
        })
      }
      
      console.log(`[LocalStorage] Imported ${importedCount} items from ${data.size} bytes`)
    } catch (error) {
      console.error('[LocalStorage] Failed to import:', error)
      
      // Emit error event
      if (this.eventEmitter) {
        this.eventEmitter.emit('storage:error', {
          error: error as Error,
          operation: 'import',
          adapter: this.getName()
        })
      }
      
      throw error
    }
  }

  async clear(): Promise<void> {
    try {
      const keysToRemove: string[] = []
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.prefix)) {
          keysToRemove.push(key)
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key))
      
      console.log(`[LocalStorage] Cleared ${keysToRemove.length} keys`)
    } catch (error) {
      console.error('[LocalStorage] Failed to clear:', error)
      
      // Emit error event
      if (this.eventEmitter) {
        this.eventEmitter.emit('storage:error', {
          error: error as Error,
          operation: 'clear',
          adapter: this.getName()
        })
      }
      
      throw error
    }
  }

  async getKeys(): Promise<string[]> {
    try {
      const keys: string[] = []
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.prefix)) {
          keys.push(key.substring(this.prefix.length))
        }
      }
      
      return keys
    } catch (error) {
      console.error('[LocalStorage] Failed to get keys:', error)
      
      // Emit error event
      if (this.eventEmitter) {
        this.eventEmitter.emit('storage:error', {
          error: error as Error,
          operation: 'getKeys',
          adapter: this.getName()
        })
      }
      
      return []
    }
  }

  getName(): string {
    return 'localStorage'
  }

  async isAvailable(): Promise<boolean> {
    try {
      const testKey = `__test_${Date.now()}__`
      localStorage.setItem(testKey, testKey)
      localStorage.removeItem(testKey)
      return true
    } catch {
      return false
    }
  }

  async getSize(): Promise<number> {
    let totalSize = 0
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(this.prefix)) {
        const value = localStorage.getItem(key)
        if (value) {
          totalSize += value.length
        }
      }
    }
    
    return totalSize
  }

  // Helper methods for date serialization
  private dateReplacer(key: string, value: any): any {
    if (value instanceof Date) {
      return { __date: value.toISOString() }
    }
    return value
  }

  private dateReviver(key: string, value: any): any {
    if (value && typeof value === 'object' && value.__date) {
      return new Date(value.__date)
    }
    return value
  }

  /**
   * F 226 — TRASH. `delete` above is unchanged and still hard.
   * These are the opt-in pair, built on the adapter's own primitives.
   */
  async softDelete(key: string, options: StorageOptions = {}): Promise<void> {
    await softDeleteVia(this as unknown as TrashPrimitives, key, options);
  }

  async restore(key: string, options: StorageOptions = {}): Promise<void> {
    await restoreVia(this as unknown as TrashPrimitives, key, options);
  }

  /** Trashed records under `prefix`, newest first. */
  async listTrash(prefix = '', options: StorageOptions = {}): Promise<unknown[]> {
    return await listTrashVia(this as unknown as TrashPrimitives, prefix, options);
  }
}